import * as THREE from 'three';
import { bus } from '@/core/EventBus';
import { buildCharacter, type CharacterRig } from '@/meshkit/character/build';
import { CharacterAnimator } from '@/meshkit/character/animator';
import { RIVAL_SOLDIER } from '@/meshkit/character/spec';
import type { CollisionWorld } from '@/world/collision';
import { bodies, moveActor } from '@/world/bodies';
import type { Faction } from '@/gameplay/GameState';
import { Weapon, type Combat, type CombatTarget, type WeaponSpec } from '@/gameplay/Combat';

export type EnemyState = 'patrol' | 'alert' | 'chase' | 'shoot' | 'searching' | 'dead';

export interface EnemyConfig {
  readonly id: number;
  readonly patrol: readonly THREE.Vector2[];
  readonly sightRange?: number;
  readonly fieldOfView?: number;
  readonly alertRadius?: number;
  readonly walkSpeed?: number;
  readonly chaseSpeed?: number;
  readonly loseSightTime?: number;
  readonly stopDistance?: number;
  readonly shootDistance?: number;
  readonly turnRate?: number;
  readonly maxHealth?: number;
  readonly weapon?: WeaponSpec;
  readonly rig?: CharacterRig;
}

export interface EnemySnapshot {
  readonly id: number;
  readonly state: EnemyState;
  readonly health: number;
  readonly position: THREE.Vector2;
  readonly facing: number;
  readonly patrolIndex: number;
}

const ENEMY_DEFAULTS = {
  sightRange: 13,
  fieldOfView: Math.PI * 0.58,
  alertRadius: 12,
  walkSpeed: 1.35,
  chaseSpeed: 2.45,
  loseSightTime: 1.35,
  stopDistance: 2.6,
  shootDistance: 8.5,
  turnRate: 9,
  maxHealth: 68,
  radius: 0.34,
} as const;

export const ENEMY_REVOLVER: WeaponSpec = {
  name: 'Revolver',
  damage: 18,
  range: 24,
  magazine: 6,
  fireInterval: 0.72,
  reloadTime: 2.2,
  spread: 0.035,
  automatic: false,
};

const WAYPOINT_EPSILON = 0.18;

export function canSeeTarget(
  from: THREE.Vector2,
  facing: number,
  target: THREE.Vector2,
  sightRange: number,
  fieldOfView: number,
  collision: CollisionWorld,
): boolean {
  const dx = target.x - from.x;
  const dz = target.y - from.y;
  const distanceSq = dx * dx + dz * dz;
  if (distanceSq > sightRange * sightRange) return false;
  if (distanceSq <= 0.0001) return true;

  const angleToTarget = Math.atan2(dx, dz);
  const delta = Math.abs(wrapAngle(angleToTarget - facing));
  if (delta > fieldOfView / 2) return false;

  return !collision.blocksLineOfSight(from, target);
}

export class Enemy implements CombatTarget {
  readonly id: number;
  readonly rig: CharacterRig;
  readonly object: THREE.Object3D;
  readonly position = new THREE.Vector2();
  readonly velocity = new THREE.Vector2();
  readonly radius = ENEMY_DEFAULTS.radius;
  readonly faction: Faction = 'barili';
  readonly weapon: Weapon;

  health: number;

  readonly #animator: CharacterAnimator;
  readonly #patrol: THREE.Vector2[];
  readonly #sightRange: number;
  readonly #fieldOfView: number;
  readonly #alertRadius: number;
  readonly #walkSpeed: number;
  readonly #chaseSpeed: number;
  readonly #loseSightTime: number;
  readonly #stopDistance: number;
  readonly #shootDistance: number;
  readonly #turnRate: number;

  readonly #desired = new THREE.Vector2();
  readonly #step = new THREE.Vector2();
  readonly #aim = new THREE.Vector2();
  readonly #lastKnownTarget = new THREE.Vector2();
  #state: EnemyState = 'patrol';
  #patrolIndex = 1;
  #facing = 0;
  #lostSight = 0;
  #unsubscribeAlert: (() => void) | null = null;

  constructor(config: EnemyConfig) {
    if (config.patrol.length === 0) throw new Error('Enemy requires at least one patrol point');

    this.id = config.id;
    this.#patrol = config.patrol.map((point) => point.clone());
    this.position.copy(this.#patrol[0]!);
    this.#sightRange = config.sightRange ?? ENEMY_DEFAULTS.sightRange;
    this.#fieldOfView = config.fieldOfView ?? ENEMY_DEFAULTS.fieldOfView;
    this.#alertRadius = config.alertRadius ?? ENEMY_DEFAULTS.alertRadius;
    this.#walkSpeed = config.walkSpeed ?? ENEMY_DEFAULTS.walkSpeed;
    this.#chaseSpeed = config.chaseSpeed ?? ENEMY_DEFAULTS.chaseSpeed;
    this.#loseSightTime = config.loseSightTime ?? ENEMY_DEFAULTS.loseSightTime;
    this.#stopDistance = config.stopDistance ?? ENEMY_DEFAULTS.stopDistance;
    this.#shootDistance = config.shootDistance ?? ENEMY_DEFAULTS.shootDistance;
    this.#turnRate = config.turnRate ?? ENEMY_DEFAULTS.turnRate;
    this.health = config.maxHealth ?? ENEMY_DEFAULTS.maxHealth;
    this.weapon = new Weapon(config.weapon ?? ENEMY_REVOLVER);
    this.rig = config.rig ?? buildCharacter(RIVAL_SOLDIER);
    this.object = this.rig.root;
    this.#animator = new CharacterAnimator(this.rig);
    this.#faceNextPatrolPoint();
    this.#sync();

    this.#unsubscribeAlert = bus.on('enemy:alerted', ({ id, position, radius }) => {
      if (id === this.id || !this.alive || this.#state !== 'patrol') return;
      const dx = this.position.x - position.x;
      const dz = this.position.y - position.z;
      if (dx * dx + dz * dz <= radius * radius) this.#state = 'alert';
    });
  }

  get alive(): boolean {
    return this.#state !== 'dead' && this.health > 0;
  }

  get state(): EnemyState {
    return this.#state;
  }

  update(dt: number, target: CombatTarget, world: CollisionWorld, combat: Combat): void {
    if (!this.alive) {
      this.#die();
      return;
    }

    this.weapon.update(dt);
    const seesTarget = canSeeTarget(
      this.position,
      this.#facing,
      target.position,
      this.#sightRange,
      this.#fieldOfView,
      world,
    );

    if (seesTarget) {
      this.#lastKnownTarget.copy(target.position);
      if (this.#state !== 'shoot') this.#becomeAlert();
    }

    switch (this.#state) {
      case 'patrol':
        this.#patrolStep(dt, world);
        break;
      case 'alert':
      case 'chase':
        this.#chaseStep(dt, target, world, seesTarget);
        break;
      case 'shoot':
        this.#shootStep(dt, target, world, combat, seesTarget);
        break;
      case 'searching':
        this.#searchStep(dt, target, world, seesTarget);
        break;
      case 'dead':
        break;
    }

    this.#animator.update(dt, this.velocity.length(), this.#state === 'shoot');
    this.#sync();
  }

  damage(amount: number): void {
    if (!this.alive) return;
    this.health = Math.max(0, this.health - amount);
    this.#becomeAlert();
    if (this.health === 0) this.#die();
  }

  snapshot(): EnemySnapshot {
    return {
      id: this.id,
      state: this.#state,
      health: this.health,
      position: this.position.clone(),
      facing: this.#facing,
      patrolIndex: this.#patrolIndex,
    };
  }

  dispose(): void {
    this.#unsubscribeAlert?.();
    this.#unsubscribeAlert = null;
    this.object.removeFromParent();
  }

  #patrolStep(dt: number, world: CollisionWorld): void {
    const next = this.#patrol[this.#patrolIndex]!;
    if (this.position.distanceTo(next) <= WAYPOINT_EPSILON) {
      this.#patrolIndex = (this.#patrolIndex + 1) % this.#patrol.length;
      this.#faceNextPatrolPoint();
      this.velocity.set(0, 0);
      return;
    }
    this.#moveToward(dt, next, this.#walkSpeed, world);
  }

  #chaseStep(dt: number, target: CombatTarget, world: CollisionWorld, seesTarget: boolean): void {
    this.#turnToward(dt, target.position);

    const distance = this.position.distanceTo(target.position);
    if (seesTarget) {
      this.#lostSight = 0;
      this.#lastKnownTarget.copy(target.position);
      if (distance <= this.#shootDistance) {
        this.#state = 'shoot';
        this.velocity.set(0, 0);
        return;
      }
    } else {
      this.#lostSight += dt;
      if (this.#lostSight >= this.#loseSightTime) {
        this.#state = 'searching';
        this.#lostSight = 0;
        return;
      }
    }

    if (distance > this.#stopDistance) this.#moveToward(dt, target.position, this.#chaseSpeed, world);
    else this.velocity.set(0, 0);
  }

  #shootStep(
    dt: number,
    target: CombatTarget,
    world: CollisionWorld,
    combat: Combat,
    seesTarget: boolean,
  ): void {
    this.velocity.set(0, 0);
    this.#turnToward(dt, target.position);

    const distance = this.position.distanceTo(target.position);
    if (!seesTarget || distance > this.#shootDistance) {
      this.#state = 'chase';
      return;
    }

    this.#lastKnownTarget.copy(target.position);
    this.#aim.copy(target.position);
    if (this.weapon.empty) this.weapon.reload();
    if (this.weapon.pullTrigger()) combat.fire(this.position, this.#aim, this.weapon.spec, this);

    if (world.blocksLineOfSight(this.position, target.position)) this.#state = 'chase';
  }

  #searchStep(dt: number, target: CombatTarget, world: CollisionWorld, seesTarget: boolean): void {
    if (seesTarget) {
      this.#lastKnownTarget.copy(target.position);
      this.#state = 'chase';
      this.#lostSight = 0;
      return;
    }

    this.#lostSight += dt;
    if (this.position.distanceTo(this.#lastKnownTarget) > this.#stopDistance) {
      this.#moveToward(dt, this.#lastKnownTarget, this.#walkSpeed, world);
    } else {
      this.velocity.set(0, 0);
    }

    if (this.#lostSight >= this.#loseSightTime * 1.8) {
      this.#state = 'patrol';
      this.#lostSight = 0;
      this.#faceNextPatrolPoint();
    }
  }

  #moveToward(dt: number, target: THREE.Vector2, speed: number, world: CollisionWorld): void {
    this.#turnToward(dt, target);
    this.#desired.subVectors(target, this.position);
    if (this.#desired.lengthSq() === 0) {
      this.velocity.set(0, 0);
      return;
    }
    this.#desired.setLength(speed);
    this.velocity.copy(this.#desired);
    this.#step.copy(this.velocity).multiplyScalar(dt);
    moveActor(this, this.#step, world, bodies);
  }

  #becomeAlert(): void {
    if (!this.alive || this.#state === 'dead') return;
    const wasPatrolling = this.#state === 'patrol';
    this.#state = 'chase';
    this.#lostSight = 0;
    this.#lastKnownTarget.copy(this.position);
    if (wasPatrolling) {
      bus.emit('enemy:alerted', {
        id: this.id,
        position: { x: this.position.x, z: this.position.y },
        radius: this.#alertRadius,
      });
    }
  }

  #die(): void {
    if (this.#state === 'dead') return;
    this.#state = 'dead';
    this.health = 0;
    this.velocity.set(0, 0);
    this.object.visible = false;
    bus.emit('enemy:killed', { id: this.id });
  }

  #faceNextPatrolPoint(): void {
    if (this.#patrol.length < 2) return;
    const next = this.#patrol[this.#patrolIndex]!;
    this.#facing = Math.atan2(next.x - this.position.x, next.y - this.position.y);
  }

  #turnToward(dt: number, target: THREE.Vector2): void {
    const targetFacing = Math.atan2(target.x - this.position.x, target.y - this.position.y);
    const delta = wrapAngle(targetFacing - this.#facing);
    this.#facing += delta * Math.min(1, this.#turnRate * dt);
  }

  #sync(): void {
    this.object.position.set(this.position.x, 0, this.position.y);
    this.object.rotation.y = this.#facing;
  }
}

function wrapAngle(angle: number): number {
  return Math.atan2(Math.sin(angle), Math.cos(angle));
}
